/**
 * Iter269.20h — Live activity ticker for /free-scan
 *
 * Polls /api/public/scan/recent-ticker every 30s and animates recent
 * anonymized scans across the page in a slim horizontal strip. Adds
 * social proof: "real people just scanned" instead of "what's this site".
 *
 * Privacy: wallet addresses are masked (0x1234…abcd), no scores exposed,
 * no account info leaked.
 *
 * Renders nothing if the endpoint returns zero items, so an empty
 * platform doesn't display a fake-looking placeholder.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Sparkles } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function timeAgo(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diff < 30)    return "just now";
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Iter269.20h — Chain color palette aligned with brand chains marquee.
const CHAIN_COLORS = {
  ethereum: "#627EEA",  bitcoin: "#F7931A",  solana:  "#9945FF",
  polygon:  "#8247E5",  arbitrum: "#28A0F0", base:    "#0052FF",
  optimism: "#FF0420",  avalanche:"#E84142", bnb:     "#F0B90B",
  cosmos:   "#2E3148",  sui:      "#4DA1F0", aptos:   "#00D2A7",
  tron:     "#FF060A",  fantom:   "#1969FF", celo:    "#FBCC5C",
  near:     "#00C08B",  cardano:  "#0033AD", default: "#94A3B8",
};

export default function FreeScanLiveTicker() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchTicker = () => {
      axios.get(`${API}/api/public/scan/recent-ticker?limit=10`)
        .then((r) => { if (alive) setItems(r.data?.items || []); })
        .catch(() => { if (alive) setItems([]); });
    };
    fetchTicker();
    const t = setInterval(fetchTicker, 30 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Empty state: hide entirely.
  if (!items || items.length === 0) return null;

  // Iter269.20h — Duplicate the list to make the CSS marquee seamless.
  const looped = [...items, ...items];

  return (
    <div className="mt-6 mb-2" data-testid="freescan-live-ticker">
      <div
        className="flex items-center gap-2 mb-2 qt-mono text-[10px]"
        style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em", textTransform: "uppercase" }}
      >
        <Activity size={11} className="qt-pulse" style={{ color: "#1FDD83" }} />
        <span>Live · last {items.length} scans · refreshed every 30s</span>
      </div>
      <div
        className="overflow-hidden rounded-xl border"
        style={{
          borderColor: "rgba(0,229,255,0.16)",
          background:  "linear-gradient(90deg, rgba(0,229,255,0.04), rgba(155,124,255,0.03))",
        }}
        data-testid="freescan-ticker-strip"
      >
        <div className="freescan-ticker-track flex items-center py-2.5">
          {looped.map((it, i) => {
            const c = CHAIN_COLORS[it.chain] || CHAIN_COLORS.default;
            return (
              <div
                key={`${it.chain}-${it.masked}-${i}`}
                className="inline-flex items-center gap-2 px-3 mx-1 whitespace-nowrap"
              >
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: c, boxShadow: `0 0 6px ${c}80` }}
                />
                <span
                  className="qt-mono text-[10px] uppercase"
                  style={{ color: c, letterSpacing: "0.12em" }}
                >
                  {it.chain}
                </span>
                <span
                  className="qt-mono text-[11px]"
                  style={{ color: "var(--qt-text-1)" }}
                >
                  {it.masked}
                </span>
                <span
                  className="qt-mono text-[9px]"
                  style={{ color: "var(--qt-text-3)", letterSpacing: "0.08em" }}
                >
                  · {timeAgo(it.ts)}
                </span>
                <Sparkles size={9} style={{ color: "rgba(0,229,255,0.35)" }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
